/**
 * − [ quantity ] +
 *
 * The buttons move by whole units, which is how most lines are changed;
 * the box still takes a decimal ('2.5' kg) typed straight in. − stops at 1:
 * a line of nothing is a line to remove, and every place this sits has a
 * Remove for that.
 *
 * `value` is a string, as an input's is, so a half-typed '2.' survives.
 *
 * `step`, when given, is a product's minimum order — 25 kg of nails. The
 * buttons then move 25, 50, 75, and a typed amount is raised to the next
 * multiple when the box is left (30 → 50), as the server does on save.
 */
export const roundUpToStep = (value, step) => {
  const n = Number(value)
  if (!step || !Number.isFinite(n) || n <= 0) return value
  const multiples = Math.max(1, Math.ceil(n / step - 1e-9))
  return String(Number((multiples * step).toFixed(3)))
}

const isMultiple = (value, step) => {
  const ratio = Number(value) / Number(step)
  return Math.abs(ratio - Math.round(ratio)) < 1e-6
}

export default function QuantityStepper({ value, onChange, label = 'Quantity', step: size, unit = '' }) {
  const by = size || 1
  const typed = Number(value)
  const offStep = Boolean(size) && Number.isFinite(typed) && typed > 0 && !isMultiple(typed, size)
  const u = unit ? ` ${unit}` : ''
  const change = (next) => onChange(next)
  const step = (delta) => {
    const next = Math.max(0, Number(value || 0) + delta * by)
    // Three decimals at most, so 0.1 + 1 never shows as 1.1000000000000001.
    change(String(Number(next.toFixed(3))))
  }

  return (
    <div className="stepper-wrap">
      <div className="stepper">
        <button
          type="button"
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={Number(value) <= by}
          onClick={() => step(-1)}
        >
          −
        </button>
        <input
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => change(e.target.value)}
          onBlur={() => {
            if (!size) return
            const rounded = roundUpToStep(value, size)
            if (rounded !== value) onChange(rounded)
          }}
        />
        <button type="button" aria-label={`Increase ${label.toLowerCase()}`} onClick={() => step(1)}>
          +
        </button>
      </div>
      {/* While typing only: leaving the box rounds it, and the note goes. */}
      {offStep && (
        <div className="stepper-note">
          Sold in multiples of {size}{u}. {Number(value)}{u} will be rounded up to{' '}
          {roundUpToStep(value, size)}{u}.
        </div>
      )}
    </div>
  )
}
